/**
 * Integration Workbench - Simplified Version
 * Bulk import for custom fields, programs, and billing activities
 */

import React, { useState } from 'react';
import { Upload, Download, Database, FileSpreadsheet, Languages, Info, CheckCircle, XCircle, AlertTriangle, Loader2, ArrowLeft, ArrowRight, Users, Building2 } from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { useAppTranslation } from '@/contexts/TranslationContext';

// Import static translations for translation export
import enTranslations from '@/src/i18n/locales/en.json';
import esTranslations from '@/src/i18n/locales/es.json';

// Import specialized wizards
import DOL5500ImportWizard from './DOL5500ImportWizard';

type EntityType = 'members' | 'program_enrollments' | 'custom_fields' | 'program_definitions' | 'billing_activities' | 'translations' | 'dol_5500_employers' | 'dol_5500_leads' | 'plan_comparison';
type ImportStep = 'select' | 'upload' | 'mapping' | 'preview' | 'execute' | 'complete';

interface FieldMapping {
  source_field: string;
  target_field: string;
  confidence: number;
  is_required: boolean;
}

interface FileAnalysis {
  file_id: string;
  file_name: string;
  total_rows: number;
  detected_headers: string[];
  suggested_mappings: FieldMapping[];
  entity_schema: Array<{
    field_name: string;
    display_name: string;
    data_type: string;
    is_required: boolean;
    description?: string;
  }>;
  sample_data: any[];
  schema_validation: {
    is_valid: boolean;
    errors: any[];
    warnings: any[];
  };
}

interface PreviewResult {
  preview_id: string;
  total_rows: number;
  rows_to_create: number;
  rows_to_skip: number;
  preview_data: any[];
  validation_result: {
    is_valid: boolean;
    errors: any[];
    warnings: any[];
  };
}

interface ImportResult {
  execution_id: string;
  status: string;
  successful_rows: number;
  failed_rows: number;
  errors: any[];
  summary: string;
}

// Flatten nested translation objects to key-value pairs
const flattenTranslations = (obj: any, prefix = ''): Record<string, string> => {
  const result: Record<string, string> = {};
  for (const key in obj) {
    const newKey = prefix ? `${prefix}.${key}` : key;
    if (typeof obj[key] === 'object' && obj[key] !== null) {
      Object.assign(result, flattenTranslations(obj[key], newKey));
    } else {
      result[newKey] = obj[key];
    }
  }
  return result;
};

interface IntegrationWorkbenchSimpleProps {
  defaultEntityType?: string;
}

export const IntegrationWorkbenchSimple: React.FC<IntegrationWorkbenchSimpleProps> = ({ defaultEntityType }) => {
  const { t } = useAppTranslation();
  const [selectedEntityType, setSelectedEntityType] = useState<EntityType | null>(
    defaultEntityType as EntityType || null
  );
  const [file, setFile] = useState<File | null>(null);
  const [currentStep, setCurrentStep] = useState<ImportStep>('select');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Import workflow state
  const [fileAnalysis, setFileAnalysis] = useState<FileAnalysis | null>(null);
  const [fieldMappings, setFieldMappings] = useState<FieldMapping[]>([]);
  const [previewResult, setPreviewResult] = useState<PreviewResult | null>(null);
  const [importResult, setImportResult] = useState<ImportResult | null>(null);

  const translationKeyCount = Object.keys(flattenTranslations(enTranslations)).length;

  // Get auth headers
  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    const cboId = localStorage.getItem('active_cbo_id');
    return {
      'Authorization': `Bearer ${token}`,
      'X-CBO-ID': cboId || '',
    };
  };

  // TRUS-428 — one row per column IN THE USER'S FILE, not one per field in the target
  // schema. Keyed the other way round, a Members import (whose schema is the core member
  // columns plus every custom field the org has defined) rendered a couple of hundred
  // rows, nearly all of them blank, and buried the handful the file actually had.
  const buildMappingRows = (analysis: FileAnalysis): FieldMapping[] => {
    const suggestedBySource = new Map(
      (analysis.suggested_mappings || [])
        .filter(m => m.source_field)
        .map(m => [m.source_field, m] as const),
    );
    const requiredByTarget = new Map((analysis.entity_schema || []).map(f => [f.field_name, f.is_required]));
    return (analysis.detected_headers || []).map(header => {
      const suggestion = suggestedBySource.get(header);
      const target = suggestion?.target_field || '';
      return {
        source_field: header,
        target_field: target,
        confidence: suggestion?.confidence ?? 0,
        is_required: requiredByTarget.get(target) ?? false,
      };
    });
  };

  // Analyze uploaded file
  const handleAnalyzeFile = async () => {
    if (!file || !selectedEntityType) return;

    setLoading(true);
    setError(null);

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await fetch(
        getWeaveinApiUrl(`/api/workbench/integrations/analyze?entity_type=${selectedEntityType}`),
        {
          method: 'POST',
          headers: getAuthHeaders(),
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }));
        let errorMessage = errorData.detail || 'Failed to analyze file';

        // Provide user-friendly error messages for common backend errors
        if (errorMessage.includes('NotNullViolation') || errorMessage.includes('null value in column')) {
          errorMessage = 'Database configuration issue. Please contact support or try again later.';
        } else if (errorMessage.includes('connection') || errorMessage.includes('timeout')) {
          errorMessage = 'Connection error. Please check your network and try again.';
        } else if (response.status === 401) {
          errorMessage = 'Session expired. Please log in again.';
        } else if (response.status === 403) {
          errorMessage = 'You do not have permission to perform this action.';
        } else if (response.status === 413) {
          errorMessage = 'File is too large. Please use a smaller file.';
        }

        throw new Error(errorMessage);
      }

      const analysis: FileAnalysis = await response.json();
      setFileAnalysis(analysis);
      setFieldMappings(buildMappingRows(analysis));
      setCurrentStep('mapping');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to analyze file');
    } finally {
      setLoading(false);
    }
  };

  // Update field mapping
  const updateFieldMapping = (index: number, targetField: string) => {
    const required = new Map((fileAnalysis?.entity_schema || []).map(f => [f.field_name, f.is_required]));
    setFieldMappings(prev => prev.map((m, i) =>
      i === index
        ? { ...m, target_field: targetField, is_required: required.get(targetField) ?? false, confidence: 0 }
        : m
    ));
  };

  // Generate preview
  const handleGeneratePreview = async () => {
    if (!fileAnalysis) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        getWeaveinApiUrl('/api/workbench/integrations/preview'),
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            file_id: fileAnalysis.file_id,
            entity_type: selectedEntityType,
            // Columns left on "-- Skip --" are dropped here rather than sent with an
            // empty target, which would land in the transformed frame as a nameless column.
            field_mappings: fieldMappings
              .filter(m => m.target_field)
              .map(m => ({
                source_field: m.source_field,
                target_field: m.target_field,
                is_required: m.is_required,
              })),
            import_mode: 'skip_errors',
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }));
        throw new Error(errorData.detail || 'Failed to generate preview');
      }

      const preview: PreviewResult = await response.json();
      setPreviewResult(preview);
      setCurrentStep('preview');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate preview');
    } finally {
      setLoading(false);
    }
  };

  // Execute import
  const handleExecuteImport = async () => {
    if (!previewResult) return;

    setLoading(true);
    setError(null);

    try {
      const response = await fetch(
        getWeaveinApiUrl('/api/workbench/integrations/execute'),
        {
          method: 'POST',
          headers: {
            ...getAuthHeaders(),
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            preview_id: previewResult.preview_id,
            confirm: true,
            import_mode: 'skip_errors',
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ detail: response.statusText }));
        // Handle Pydantic validation errors (422) which return detail as array
        let errorMessage = 'Failed to execute import';
        if (Array.isArray(errorData.detail)) {
          errorMessage = errorData.detail.map((e: any) => e.msg || e.message || JSON.stringify(e)).join('; ');
        } else if (typeof errorData.detail === 'string') {
          errorMessage = errorData.detail;
        } else if (errorData.detail) {
          errorMessage = JSON.stringify(errorData.detail);
        }
        console.error('Execute import error:', errorData);
        throw new Error(errorMessage);
      }

      const result: ImportResult = await response.json();
      setImportResult(result);
      setCurrentStep('complete');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to execute import');
    } finally {
      setLoading(false);
    }
  };

  // Reset workflow
  const handleReset = () => {
    setSelectedEntityType(null);
    setFile(null);
    setCurrentStep('select');
    setFileAnalysis(null);
    setFieldMappings([]);
    setPreviewResult(null);
    setImportResult(null);
    setError(null);
  };

  const entityTypes = [
    // TRUS-428 — Members and Program Enrollments were already valid entity types on the
    // API but had never been offered here, so an org onboarding its first roster had no
    // way to reach the importer.
    {
      value: 'members',
      label: t('integrations.members', 'Members'),
      description: t('integrations.membersDescription', 'Import a roster of members, their custom field values, and their program enrollment'),
      icon: <Users className="h-6 w-6" />,
    },
    {
      value: 'program_enrollments',
      label: t('integrations.programEnrollments', 'Program Enrollments'),
      description: t('integrations.programEnrollmentsDescription', 'Enroll members who are already in WeaveIn into a program'),
      icon: <Users className="h-6 w-6" />,
    },
    {
      value: 'custom_fields',
      label: t('integrations.customFields', 'Custom Fields'),
      description: t('integrations.customFieldsDescription', 'Import custom field definitions to extend your data model'),
      icon: <Database className="h-6 w-6" />,
    },
    {
      value: 'program_definitions',
      label: t('integrations.programDefinitions', 'Program Definitions'),
      description: t('integrations.programDefinitionsDescription', 'Import program configurations, eligibility criteria, and settings'),
      icon: <FileSpreadsheet className="h-6 w-6" />,
    },
    {
      value: 'billing_activities',
      label: t('integrations.billingActivities', 'Billing Activities'),
      description: t('integrations.billingActivitiesDescription', 'Import billing codes, rates, and activity definitions'),
      icon: <FileSpreadsheet className="h-6 w-6" />,
    },
    {
      value: 'translations',
      label: t('integrations.translationKeys', 'Translation Keys'),
      description: t('integrations.translationKeysDescription', 'Download/upload translation keys for multi-language support'),
      icon: <Languages className="h-6 w-6" />,
    },
    // Contact Sync Integration Types
    {
      value: 'contacts_google',
      label: t('integrations.contactsGoogle', 'Google Contacts'),
      description: t('integrations.contactsGoogleDescription', 'Sync contacts from Google Workspace/Gmail'),
      icon: <Users className="h-6 w-6" />,
    },
    {
      value: 'contacts_outlook',
      label: t('integrations.contactsOutlook', 'Outlook Contacts'),
      description: t('integrations.contactsOutlookDescription', 'Sync contacts from Microsoft Outlook/Exchange'),
      icon: <Users className="h-6 w-6" />,
    },
    {
      value: 'contacts_crm',
      label: t('integrations.contactsCrm', 'CRM Contacts'),
      description: t('integrations.contactsCrmDescription', 'Import contacts from Salesforce, HubSpot, or other CRM'),
      icon: <Users className="h-6 w-6" />,
    },
    {
      value: 'contacts_csv',
      label: t('integrations.contactsCsv', 'CSV/vCard Import'),
      description: t('integrations.contactsCsvDescription', 'Import contacts from CSV files or vCard exports'),
      icon: <FileSpreadsheet className="h-6 w-6" />,
    },
    // Government Data Sources
    {
      value: 'dol_5500_employers',
      label: t('integrations.dol5500Employers', 'DOL Form 5500 (Employers)'),
      description: t('integrations.dol5500EmployersDescription', 'Import self-insured employer data from DOL Form 5500 EFAST2 filings'),
      icon: <Building2 className="h-6 w-6" />,
    },
    {
      value: 'dol_5500_leads',
      label: t('integrations.dol5500Leads', 'DOL Form 5500 (Lead Discovery)'),
      description: t('integrations.dol5500LeadsDescription', 'Import DOL Form 5500 data for lead discovery and sales prospecting'),
      icon: <Building2 className="h-6 w-6" />,
    },
    {
      value: 'plan_comparison',
      label: t('integrations.planComparison', 'Public Plan Comparison'),
      description: t('integrations.planComparisonDescription', 'Import public plan data from CMS, state exchanges, or benchmark sources'),
      icon: <Database className="h-6 w-6" />,
    },
  ];

  // Download translations as CSV or Excel (client-side)
  const handleDownloadTranslations = (format: 'csv' | 'xlsx') => {
    const enFlat = flattenTranslations(enTranslations);
    const esFlat = flattenTranslations(esTranslations);

    // Combine all keys
    const allKeys = new Set([...Object.keys(enFlat), ...Object.keys(esFlat)]);
    const translations: { key: string; en: string; es: string }[] = [];

    allKeys.forEach(key => {
      translations.push({
        key,
        en: enFlat[key] || '',
        es: esFlat[key] || '',
      });
    });

    // Sort by key for better organization
    translations.sort((a, b) => a.key.localeCompare(b.key));

    if (format === 'csv') {
      // Create CSV content
      const headers = ['key', 'en', 'es'];
      const rows = translations.map(tr => [
        `"${tr.key}"`,
        `"${(tr.en || '').replace(/"/g, '""')}"`,
        `"${(tr.es || '').replace(/"/g, '""')}"`,
      ].join(','));

      const csvContent = [headers.join(','), ...rows].join('\n');

      // Create download link
      const blob = new Blob(['\ufeff' + csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `translations_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } else {
      // Create TSV content (Excel-compatible)
      const headers = ['key', 'en', 'es'];
      const rows = translations.map(tr => [
        tr.key,
        tr.en || '',
        tr.es || '',
      ].join('\t'));

      const tsvContent = [headers.join('\t'), ...rows].join('\n');

      // Create download link
      const blob = new Blob(['\ufeff' + tsvContent], { type: 'text/tab-separated-values;charset=utf-8;' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `translations_${new Date().toISOString().split('T')[0]}.tsv`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  const handleDownloadTemplate = async (format: 'csv' | 'xlsx') => {
    if (!selectedEntityType) return;

    // Handle translations with client-side export
    if (selectedEntityType === 'translations') {
      handleDownloadTranslations(format);
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      if (!token) {
        console.error('No auth token found');
        alert('Authentication required. Please log in again.');
        return;
      }

      const cboId = localStorage.getItem('active_cbo_id');
      if (!cboId) {
        console.error('No active organization found');
        alert('Please select an organization first.');
        return;
      }

      const response = await fetch(
        getWeaveinApiUrl(`/api/workbench/integrations/templates/${selectedEntityType}?format=${format}`),
        {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json',
            'X-CBO-ID': cboId,
          },
        }
      );

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${selectedEntityType}_template.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
      } else {
        const errorText = await response.text();
        console.error('Error downloading template:', response.status, errorText);
        alert(`Failed to download template: ${response.status} ${response.statusText}`);
      }
    } catch (error) {
      console.error('Error downloading template:', error);
      alert('An error occurred while downloading the template. Please try again.');
    }
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      {/* Header */}
      <div className="mb-8">
        <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">
          {t('integrations.bulkImport', 'Bulk Import Workbench')}
        </h2>
        <p className="text-gray-600 dark:text-gray-400">
          {t('integrations.workbench', 'Import custom fields, program definitions, and billing activities from CSV/Excel files')}
        </p>
      </div>

      {/* Entity Type Selection */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
        <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
          {t('integrations.selectImportType', 'Select What to Import')}
        </h3>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {entityTypes.map((type) => (
            <button
              key={type.value}
              onClick={() => setSelectedEntityType(type.value as EntityType)}
              className={`p-6 rounded-lg border-2 text-left transition-all ${
                selectedEntityType === type.value
                  ? 'border-teal-600 bg-teal-50 dark:bg-teal-900/20'
                  : 'border-gray-200 dark:border-gray-700 hover:border-teal-300'
              }`}
            >
              <div className="flex items-center gap-3 mb-3">
                <div className={`${
                  selectedEntityType === type.value ? 'text-teal-600' : 'text-gray-400'
                }`}>
                  {type.icon}
                </div>
                <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                  {type.label}
                </h4>
              </div>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                {type.description}
              </p>
            </button>
          ))}
        </div>
      </div>

      {/* DOL 5500 Specialized Wizard */}
      {selectedEntityType && (selectedEntityType === 'dol_5500_employers' || selectedEntityType === 'dol_5500_leads') && (
        <DOL5500ImportWizard
          targetTable={selectedEntityType === 'dol_5500_employers' ? 'employers' : 'lb_company_matches'}
          onComplete={() => {
            setSelectedEntityType(null);
          }}
        />
      )}

      {/* Template Download - for non-DOL entity types */}
      {selectedEntityType && selectedEntityType !== 'dol_5500_employers' && selectedEntityType !== 'dol_5500_leads' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
            {t('integrations.downloadTemplate', 'Download Template')}
          </h3>
          <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
            {t('messages.no_data', 'Start with a pre-formatted template with sample data and field descriptions')}
          </p>
          <div className="flex gap-4">
            <button
              onClick={() => handleDownloadTemplate('csv')}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              {t('integrations.downloadCSV', 'Download CSV Template')}
            </button>
            <button
              onClick={() => handleDownloadTemplate('xlsx')}
              className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              <Download className="h-4 w-4" />
              {t('integrations.downloadExcel', 'Download Excel Template')}
            </button>
          </div>
        </div>
      )}

      {/* File Upload - for non-DOL entity types */}
      {selectedEntityType && selectedEntityType !== 'dol_5500_employers' && selectedEntityType !== 'dol_5500_leads' && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-900 dark:text-gray-100">
            {t('integrations.uploadFile', 'Upload File')}
          </h3>
          <div className="border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg p-12 text-center">
            <Upload className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            {file ? (
              <div>
                <FileSpreadsheet className="h-8 w-8 text-green-600 mx-auto mb-2" />
                <p className="font-medium text-gray-900 dark:text-gray-100">{file.name}</p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {(file.size / 1024).toFixed(2)} KB
                </p>
              </div>
            ) : (
              <div>
                <p className="text-lg mb-2 text-gray-700 dark:text-gray-300">
                  {t('integrations.dragAndDrop', 'Drag and drop file here, or click to browse')}
                </p>
                <p className="text-sm text-gray-500 dark:text-gray-400">
                  {t('integrations.supportedFormats', 'Supports CSV, XLSX, XLS files')}
                </p>
              </div>
            )}
            <input
              type="file"
              accept=".csv,.xlsx,.xls"
              onChange={(e) => {
                if (e.target.files && e.target.files[0]) {
                  setFile(e.target.files[0]);
                }
              }}
              className="hidden"
              id="file-upload"
            />
            <label
              htmlFor="file-upload"
              className="inline-block mt-4 px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 cursor-pointer transition-colors"
            >
              {t('integrations.browseFiles', 'Browse Files')}
            </label>
          </div>

          {file && (
            <div className="mt-6">
              <button
                onClick={handleAnalyzeFile}
                disabled={loading}
                className="w-full px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors font-medium disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {loading ? (
                  <>
                    <Loader2 className="h-5 w-5 animate-spin" />
                    {t('integrations.analyzing', 'Analyzing...')}
                  </>
                ) : (
                  <>
                    <ArrowRight className="h-5 w-5" />
                    {t('integrations.analyzeFile', 'Analyze File & Continue')}
                  </>
                )}
              </button>
            </div>
          )}
        </div>
      )}

      {/* Step 2: Field Mapping */}
      {currentStep === 'mapping' && fileAnalysis && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
              {t('integrations.fieldMapping', 'Field Mapping')}
            </h3>
            <span className="text-sm text-gray-500">
              {fileAnalysis.total_rows} rows detected
            </span>
          </div>

          {/* Validation Status */}
          {fileAnalysis.schema_validation && (
            <div className={`mb-4 p-3 rounded-lg ${
              fileAnalysis.schema_validation.is_valid
                ? 'bg-green-50 dark:bg-green-900/20 border border-green-200'
                : 'bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200'
            }`}>
              <div className="flex items-center gap-2">
                {fileAnalysis.schema_validation.is_valid ? (
                  <CheckCircle className="h-5 w-5 text-green-600" />
                ) : (
                  <AlertTriangle className="h-5 w-5 text-yellow-600" />
                )}
                <span className={fileAnalysis.schema_validation.is_valid ? 'text-green-800' : 'text-yellow-800'}>
                  {fileAnalysis.schema_validation.is_valid
                    ? 'File structure validated successfully'
                    : `${fileAnalysis.schema_validation.errors.length} validation issues found`}
                </span>
              </div>
            </div>
          )}

          {/* Field Mapping Table */}
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="text-left p-3 font-medium">Source Column</th>
                  <th className="text-left p-3 font-medium">Target Field</th>
                  <th className="text-left p-3 font-medium">Confidence</th>
                  <th className="text-left p-3 font-medium">Required</th>
                </tr>
              </thead>
              <tbody>
                {fieldMappings.map((mapping, index) => (
                  <tr key={index} className="border-t dark:border-gray-700">
                    <td className="p-3 font-mono text-sm">{mapping.source_field}</td>
                    <td className="p-3">
                      <select
                        value={mapping.target_field}
                        onChange={(e) => updateFieldMapping(index, e.target.value)}
                        className="w-full p-2 border rounded-md dark:bg-gray-700 dark:border-gray-600"
                      >
                        <option value="">-- Skip --</option>
                        {fileAnalysis.entity_schema.map((field) => (
                          <option key={field.field_name} value={field.field_name}>
                            {field.display_name} ({field.field_name})
                          </option>
                        ))}
                      </select>
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-1 rounded-full text-xs ${
                        mapping.confidence >= 80 ? 'bg-green-100 text-green-800' :
                        mapping.confidence >= 50 ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {mapping.confidence}%
                      </span>
                    </td>
                    <td className="p-3">
                      {mapping.is_required && (
                        <span className="text-red-600 font-medium">Required</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Navigation Buttons */}
          <div className="flex justify-between mt-6">
            <button
              onClick={() => setCurrentStep('select')}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back
            </button>
            <button
              onClick={handleGeneratePreview}
              disabled={loading}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating Preview...
                </>
              ) : (
                <>
                  Preview Import
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Preview */}
      {currentStep === 'preview' && previewResult && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-4">
            {t('integrations.importPreview', 'Import Preview')}
          </h3>

          {/* Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="p-4 bg-gray-50 dark:bg-gray-700 rounded-lg text-center">
              <div className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                {previewResult.total_rows}
              </div>
              <div className="text-sm text-gray-500">Total Rows</div>
            </div>
            <div className="p-4 bg-green-50 dark:bg-green-900/20 rounded-lg text-center">
              <div className="text-2xl font-bold text-green-600">
                {previewResult.rows_to_create}
              </div>
              <div className="text-sm text-green-700">To Import</div>
            </div>
            <div className="p-4 bg-yellow-50 dark:bg-yellow-900/20 rounded-lg text-center">
              <div className="text-2xl font-bold text-yellow-600">
                {previewResult.rows_to_skip}
              </div>
              <div className="text-sm text-yellow-700">To Skip</div>
            </div>
          </div>

          {/* Preview Data */}
          {previewResult.preview_data.length > 0 && (
            <div className="mb-6">
              <h4 className="font-medium mb-2">Sample Data (First 5 rows - Transformed)</h4>
              <div className="overflow-x-auto border rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 dark:bg-gray-700">
                    <tr>
                      <th className="text-left p-2 font-medium">Row</th>
                      {previewResult.preview_data[0]?.transformed &&
                        Object.keys(previewResult.preview_data[0].transformed).slice(0, 5).map((key) => (
                          <th key={key} className="text-left p-2 font-medium">{key}</th>
                        ))}
                    </tr>
                  </thead>
                  <tbody>
                    {previewResult.preview_data.slice(0, 5).map((row, idx) => (
                      <tr key={idx} className="border-t">
                        <td className="p-2 text-gray-500">{row.row_number || idx + 1}</td>
                        {row.transformed && Object.entries(row.transformed).slice(0, 5).map(([key, value]) => (
                          <td key={key} className="p-2 truncate max-w-xs">
                            {value === null || value === undefined ? '' : String(value)}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Validation Errors */}
          {previewResult.validation_result.errors.length > 0 && (
            <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-lg">
              <h4 className="font-medium text-red-800 dark:text-red-200 mb-2">
                Validation Errors ({previewResult.validation_result.errors.length})
              </h4>
              <ul className="text-sm text-red-700 dark:text-red-300 space-y-1 max-h-40 overflow-y-auto">
                {previewResult.validation_result.errors.slice(0, 10).map((err, idx) => (
                  <li key={idx}>Row {err.row_number}: {err.message || err.error}</li>
                ))}
                {previewResult.validation_result.errors.length > 10 && (
                  <li className="font-medium">... and {previewResult.validation_result.errors.length - 10} more</li>
                )}
              </ul>
            </div>
          )}

          {/* Navigation Buttons */}
          <div className="flex justify-between">
            <button
              onClick={() => setCurrentStep('mapping')}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 flex items-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to Mapping
            </button>
            <button
              onClick={handleExecuteImport}
              disabled={loading || previewResult.rows_to_create === 0}
              className="px-6 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 flex items-center gap-2 disabled:opacity-50"
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Importing...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4" />
                  Execute Import ({previewResult.rows_to_create} rows)
                </>
              )}
            </button>
          </div>
        </div>
      )}

      {/* Step 4: Complete */}
      {currentStep === 'complete' && importResult && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm p-6 mb-6">
          <div className="text-center py-8">
            {importResult.status === 'completed' ? (
              <CheckCircle className="h-16 w-16 text-green-500 mx-auto mb-4" />
            ) : (
              <AlertTriangle className="h-16 w-16 text-yellow-500 mx-auto mb-4" />
            )}
            <h3 className="text-xl font-semibold mb-2">
              {importResult.status === 'completed' ? 'Import Successful!' : 'Import Completed with Issues'}
            </h3>
            <p className="text-gray-600 dark:text-gray-400 mb-6">
              {importResult.summary}
            </p>

            <div className="flex justify-center gap-8 mb-6">
              <div className="text-center">
                <div className="text-3xl font-bold text-green-600">{importResult.successful_rows}</div>
                <div className="text-sm text-gray-500">Imported</div>
              </div>
              {importResult.failed_rows > 0 && (
                <div className="text-center">
                  <div className="text-3xl font-bold text-red-600">{importResult.failed_rows}</div>
                  <div className="text-sm text-gray-500">Failed</div>
                </div>
              )}
            </div>

            {/* Errors */}
            {importResult.errors.length > 0 && (
              <div className="text-left mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-lg max-h-48 overflow-y-auto">
                <h4 className="font-medium text-red-800 mb-2">Errors</h4>
                <ul className="text-sm text-red-700 space-y-1">
                  {importResult.errors.slice(0, 20).map((err, idx) => (
                    <li key={idx}>Row {err.row_number}: {err.error}</li>
                  ))}
                </ul>
              </div>
            )}

            <button
              onClick={handleReset}
              className="px-6 py-3 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors"
            >
              Start New Import
            </button>
          </div>
        </div>
      )}

      {/* Error Display */}
      {error && (
        <div className="mb-6 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg">
          <div className="flex items-center gap-2">
            <XCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-800 dark:text-red-200">{error}</span>
          </div>
        </div>
      )}

      {/* Info Box - only show on initial steps */}
      {(currentStep === 'select' || currentStep === 'upload') && (
        <div className="mt-6 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <h4 className="font-semibold text-blue-900 dark:text-blue-100 mb-2">
            {t('integrations.howItWorks', 'How It Works')}
          </h4>
          <ol className="text-sm text-blue-800 dark:text-blue-200 space-y-1 list-decimal list-inside">
            <li>{t('integrations.step1', 'Select the type of data you want to import')}</li>
            <li>{t('integrations.step2', 'Download the template for your selected type')}</li>
            <li>{t('integrations.step3', 'Fill in the template with your data')}</li>
            <li>{t('integrations.step4', 'Upload the completed file')}</li>
            <li>{t('integrations.step5', 'Review field mappings and preview data')}</li>
            <li>{t('integrations.step6', 'Execute the import')}</li>
          </ol>
        </div>
      )}
    </div>
  );
};

export default IntegrationWorkbenchSimple;
